/**
 * Methodology — `/methodology`. Public statement of how we source,
 * verify and tier data. This page IS the trust asset — the thing a
 * journalist writing about WUA cites, the thing a school counsellor
 * shows the head of sixth form to justify recommending us over UCAS,
 * the thing that separates a comparison site from a ranking site.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShieldCheck, ScrollText, AlertTriangle, Compass, ExternalLink } from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";
import { api } from "@/lib/api";


/** Prettify an ISO / date string into "Aug 2025" — falls back to raw
 * input if parsing fails. Arrays / non-strings return null so the UI
 * keeps its editorial fallback date. */
function _fmtFreshness(iso) {
  if (!iso || typeof iso !== "string") return null;
  const d = new Date(iso.length === 10 ? iso + "T00:00:00Z" : iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}


export default function Methodology() {
  const [freshness, setFreshness] = useState({});
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    api.get("/methodology/source-freshness")
      .then((r) => setFreshness(r.data?.sources || {}))
      .catch(() => { /* fall back to editorial dates */ });
    api.get("/methodology/coverage")
      .then((r) => setCoverage(r.data))
      .catch(() => setCoverage({ rows: [], totals: {} }));
  }, []);

  const live = (key) => _fmtFreshness(freshness[key]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10" data-testid="methodology-page">
      <PathwaySEO
        title="How WhichUniversityAdvisor sources, verifies and tiers its data"
        description="Every number on this site carries a source. We publish the four-tier data taxonomy (editorial / verified / directory / non-HE), our sourcing rules, our error-correction policy, and the list of corrections we've made this year."
        path="/methodology"
      />

      <div>
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <ScrollText className="h-3 w-3" /> Methodology
        </div>
        <h1
          data-testid="methodology-heading"
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          How we source, verify and tier our data
        </h1>
        <p className="mt-3 text-stone-700 max-w-3xl leading-relaxed text-lg">
          WhichUniversityAdvisor is a comparison site, not a ranking
          site. Every number on the site — acceptance rate, tuition,
          starting salary, cohort-employer overlap — is sourced from a
          named public dataset with a dated citation. This page tells
          you exactly which datasets, how we join them, and what
          happens when we get something wrong.
        </p>
      </div>

      <Section id="coverage" title="Live coverage tracker">
        <p className="text-[14px]">
          We publish our real-time reach across the 20 countries we
          cover so you can see, before you trust a number, how much
          of the target universe we've actually indexed. This table
          re-reads Mongo on every page load — no manual copy-paste.
          Benchmarks last reviewed{" "}
          <span className="font-mono text-stone-500">
            {coverage?.reviewed || "—"}
          </span>.
        </p>
        {coverage?.totals && (
          <div
            data-testid="methodology-coverage-totals"
            className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            <StatTile label="Countries live" value={coverage.totals.countries_covered} />
            <StatTile label="Universities" value={`${coverage.totals.universities_have?.toLocaleString()}`} sub={`${coverage.totals.universities_pct}% of target`} />
            <StatTile label="Degrees" value={`${coverage.totals.degrees_have?.toLocaleString()}`} sub={`${coverage.totals.degrees_pct}% of target`} />
            <StatTile
              label="Editorial fields"
              value={coverage.rows?.length > 0 ? `${(coverage.rows.reduce((s,r)=>s+r.depth_pct,0)/coverage.rows.length).toFixed(0)}%` : "—"}
              sub={
                coverage.rows?.length > 0
                  ? `${(coverage.rows.reduce((s,r)=>s+(r.depth_pct_verified||0),0)/coverage.rows.length).toFixed(0)}% verified per row`
                  : "avg depth across countries"
              }
            />
          </div>
        )}
        {coverage?.rows?.length > 0 && (
          <div className="mt-4 overflow-x-auto rounded-lg border border-stone-200">
            <table
              data-testid="methodology-coverage-table"
              className="w-full text-[13px]"
            >
              <thead className="bg-stone-50 text-stone-600 text-[10px] font-underground-sc tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Country</th>
                  <th className="text-left px-3 py-2 font-normal">Universities</th>
                  <th className="text-left px-3 py-2 font-normal">Degrees</th>
                  <th className="text-left px-3 py-2 font-normal" title="Any of the six tracked fields populated on the row — includes both official per-row data and indicative tier/country maps">Editorial depth</th>
                  <th className="text-left px-3 py-2 font-normal" title="Only counts fields traced to an official per-row source (LEO5, Scorecard 5yr earnings, HESA typical-entry, per-uni tuition + deadline URLs). Indicative fills excluded.">Verified per row</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {coverage.rows.map((r) => (
                  <tr
                    key={r.country}
                    data-testid={`methodology-coverage-row-${r.country.replace(/\s+/g,"-").toLowerCase()}`}
                  >
                    <td className="px-3 py-2 font-medium text-[#002147]">{r.country}</td>
                    <td className="px-3 py-2 font-mono tabular-nums">
                      {r.universities_have.toLocaleString()} / {r.universities_estimated.toLocaleString()}
                      <span className="ml-1.5 text-stone-500 text-[11px]">{r.universities_pct}%</span>
                    </td>
                    <td className="px-3 py-2 font-mono tabular-nums">
                      {r.degrees_have.toLocaleString()} / {r.degrees_estimated.toLocaleString()}
                      <span className="ml-1.5 text-stone-500 text-[11px]">{r.degrees_pct}%</span>
                    </td>
                    <td className="px-3 py-2 font-mono tabular-nums">{r.depth_pct}%</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-emerald-800">
                      {(r.depth_pct_verified ?? 0).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-[12px] text-stone-500 leading-relaxed">
          Target universes come from the national ministries + regulators
          themselves (UCAS · NCES IPEDS · MIUR · DAAD · CampusFrance · HEA
          Ireland · DGES Portugal · MECD Spain · TEQSA · Universities Canada
          · UGC Hong Kong).{" "}
          <strong className="text-stone-700">Editorial depth</strong> counts
          any of the six tracked fields populated on the row — includes both
          official per-row data (LEO5, Scorecard 5yr earnings, HESA typical-
          entry) and defensible tier/subject/country-cycle baselines that
          every row cites its source for. The{" "}
          <strong className="text-emerald-800">Verified per row</strong>{" "}
          column strips out the baseline fills and only counts fields traced
          to an official per-row source — a strictly more conservative number
          that mirrors what a human editorial pass would sign off. We publish
          both so you can trust the numbers on their own terms.
        </p>
      </Section>

      <Section id="tiers" title="The four data tiers">
        <p>
          Every university and every degree in our catalog sits in one
          of four tiers. The tier is always visible on the page —
          either as the default (editorial), a neutral category badge
          (specialist / FE college), or a prominent amber pill
          (directory listing without an editorial review yet).
        </p>
        <ul className="mt-4 space-y-3 text-[14px]">
          <TierRow
            name="Editorial"
            colour="bg-emerald-100 text-emerald-900 border-emerald-300"
            body="Hand-curated write-up + full facts + notable-alumni verified via Wikipedia or the institution's own alumni page. All 519 UK / US / IE / NL / DE / FR / AU flagships sit here."
          />
          <TierRow
            name="Verified"
            colour="bg-sky-100 text-sky-900 border-sky-300"
            body="Facts sourced from public regulator data (HESA, US College Scorecard, DUO, CAO, MUR, HEA). No editorial write-up yet, but every number is defensible in a footnote. 1,919 unis."
          />
          <TierRow
            name="Directory (with editorial write-up)"
            colour="bg-amber-100 text-amber-900 border-amber-300"
            body="UK FE colleges + specialist HE providers. Our editors draft each write-up strictly from a fact-sheet pulled from public sources (student count, mission group, campus location, alumni page). The top 20% by student count get a second editor pass. Marked with a neutral 'Further education college' badge, not the ranking-site treatment."
          />
          <TierRow
            name="Non-HE entity"
            colour="bg-stone-100 text-stone-700 border-stone-300"
            body="Local councils, NHS trusts and other public bodies that hold a HESA URN but aren't higher-education providers. Excluded from all public browse and search surfaces."
          />
        </ul>
      </Section>

      <Section id="sources" title="Data sources by field">
        <p className="text-[13px] text-stone-600 mb-3">
          Every degree row also carries a per-course "Verify at source" panel
          linking to its exact HESA / UCAS / DAAD / Scorecard page — click any
          degree to audit it in one tap.
        </p>
        <table className="mt-3 w-full text-[13px] border-collapse" data-testid="methodology-sources-table">
          <thead>
            <tr className="text-left border-b border-stone-200 font-underground-sc text-[10px] text-stone-500 tracking-wider">
              <th className="py-2 pr-2">Field</th>
              <th className="py-2 pr-2">Source</th>
              <th className="py-2 pr-2">Refresh</th>
              <th className="py-2">Last updated</th>
            </tr>
          </thead>
          <tbody className="text-stone-700">
            <SourceRow field="UK degree catalogue" source="HESA Discover Uni (OGL v3.0)" url="https://discoveruni.gov.uk/" refresh="Annual (KIS return)" lastUpdated={live("uk-hesa") || "Aug 2025"} />
            <SourceRow field="UK grad salary" source="Longitudinal Educational Outcomes (LEO), gov.uk / DfE" url="https://www.gov.uk/government/collections/statistics-on-higher-education-graduates" refresh="Annual (HMRC-linked, ~3y lag)" lastUpdated={live("leo") || "Jul 2025 (tax yr 2020/21)"} />
            <SourceRow field="UK cohort employers" source="HESA Graduate Outcomes 2021/22" url="https://www.hesa.ac.uk/data-and-analysis/graduates" refresh="Annual" lastUpdated={live("hesa-graduate-outcomes") || "Jun 2025"} />
            <SourceRow field="UK tariff distributions" source="Discover Uni / Office for Students" url="https://www.officeforstudents.org.uk/data-and-analysis/discover-uni/" refresh="Annual" lastUpdated={live("uk-hesa") || "Aug 2025"} />
            <SourceRow field="UK UCAS course-code verification" source="digital.ucas.com Algolia + live search" url="https://digital.ucas.com/coursedisplay" refresh="Weekly (per-degree re-check)" lastUpdated={live("ucas-verify") || "This week"} />
            <SourceRow field="UK visa sponsor licence" source="Home Office Register of Licensed Sponsors" url="https://www.gov.uk/government/publications/register-of-licensed-sponsors-students" refresh="Monthly" lastUpdated={live("visa-sponsor") || "Feb 2026"} />
            <SourceRow field="US universities + degrees + earnings" source="US Dept of Ed College Scorecard API (public domain)" url="https://collegescorecard.ed.gov/data/api-documentation" refresh="Monthly" lastUpdated={live("us-scorecard") || "Feb 2026"} />
            <SourceRow field="German (English-taught) degrees" source="DAAD International Programmes (open publication)" url="https://www2.daad.de/deutschland/studienangebote/international-programmes/" refresh="Quarterly" lastUpdated={live("de-daad") || "Jan 2026"} />
            <SourceRow field="Dutch degrees (WO + HBO)" source="DUO CROHO (Dutch Ministry of Education, CC0)" url="https://duo.nl/open_onderwijsdata/" refresh="Annual" lastUpdated={live("nl-duo") || "Sep 2025"} />
            <SourceRow field="Finnish degrees" source="Studyinfo.fi (Finnish National Agency for Education)" url="https://studyinfo.fi/" refresh="Annual" lastUpdated="Sep 2025" />
            <SourceRow field="Danish universities" source="Statistics Denmark (DST)" url="https://www.dst.dk/" refresh="Annual" lastUpdated="Sep 2025" />
            <SourceRow field="Italian universities" source="MUR Universitaly (Italian Ministry of University & Research)" url="https://www.universitaly.it/" refresh="Annual" lastUpdated="Sep 2025" />
            <SourceRow field="Irish admissions points" source="CAO Points + HEA First Destinations" url="https://www.cao.ie/" refresh="Annual (post-Aug results)" lastUpdated="Sep 2025 (2025 cycle)" />
            <SourceRow field="Australian employment/salary" source="QILT Graduate Outcomes Survey (UG-FT)" url="https://www.qilt.edu.au/" refresh="Annual" lastUpdated="Nov 2025" />
            <SourceRow field="UK Ofsted rating (FE)" source="DfE Get Information about Schools CSV" url="https://get-information-schools.service.gov.uk/" refresh="Monthly" lastUpdated="Feb 2026" />
            <SourceRow field="Cost-of-living (city)" source="Kaggle Numbeo mirror + hand-curated corrections" refresh="Monthly" lastUpdated={live("col-city") || "Feb 2026"} />
            <SourceRow field="Curated firm salary bands" source="Levels.fyi + Glassdoor + firm careers pages (see Editorial curation below)" url="https://www.levels.fyi/" refresh="Quarterly (as-of chip on card)" lastUpdated="Dec 2025" />
          </tbody>
        </table>
      </Section>

      <Section id="employment-sources" title="Where graduate employment data comes from — by region">
        <p className="text-[13px]">
          Cohort payroll data isn't published centrally in most of the
          world. Where an official register exists (UK LEO, US Scorecard,
          AU QILT), we lead with it. Where it doesn't, our editors work
          firm careers pages + regional Glassdoor / Emolument / Kununu /
          native salary sites, cross-checking against at least two
          independent readings before a band lands on a firm profile.
          Every source below is public and linkable.
        </p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
          <RegionSource flag="🇬🇧" region="United Kingdom" primary="HESA LEO 3y → 5y (HMRC-linked)" secondary="Discover Uni + HESA Graduate Outcomes 2021/22 + Legal Cheek trainee/NQ bands" />
          <RegionSource flag="🇺🇸" region="United States" primary="US Dept of Ed College Scorecard — median earnings 1/3/5y after entry" secondary="Handshake + firm-published pay bands (Levels.fyi for tech, Management Consulted for consulting, Vault for law)" />
          <RegionSource flag="🇮🇪" region="Ireland" primary="HEA First Destinations Survey (national, cohort-wide)" secondary="Legal Cheek Ireland (trainee/NQ bands) + Fenero / Morgan McKinley salary guides" />
          <RegionSource flag="🇦🇺" region="Australia" primary="QILT Graduate Outcomes Survey (UG-FT, ~120,000 respondents/year)" secondary="Payscale AU + Australian Financial Review grad-salary league table" />
          <RegionSource flag="🇩🇪" region="Germany" primary="Editorial · Kununu + StepStone Gehaltsatlas + firm careers pages" secondary="No unified LEO equivalent; DAAD publishes only median across all graduates" />
          <RegionSource flag="🇫🇷" region="France" primary="Editorial · Glassdoor France + APEC salary observatory + Emolument.com" secondary="INSEE publishes only sector-level medians, not firm-level" />
          <RegionSource flag="🇮🇹" region="Italy" primary="Editorial · AlmaLaurea occupational profiles + Glassdoor Italia" secondary="AlmaLaurea covers ~76 unis' UG cohorts; combines with firm-published intern bands" />
          <RegionSource flag="🇪🇸" region="Spain" primary="Editorial · Glassdoor Iberia + Emolument Madrid + INE encuesta salarial" secondary="MECD publishes only median across all graduates by degree class" />
          <RegionSource flag="🇵🇹" region="Portugal" primary="Editorial · Kununu Iberia + Glassdoor Portugal + Landing.jobs (tech)" secondary="DGES only publishes admission data, not outcomes" />
          <RegionSource flag="🇳🇱" region="Netherlands" primary="CBS StatLine + WO Nationale Alumni Enquête" secondary="Employer bands cross-checked against Emolument.com Amsterdam" />
          <RegionSource flag="🇨🇭" region="Switzerland" primary="Editorial · Swiss FSO + Emolument Zürich + firm careers pages (UBS, CS legacy, Roche, Novartis)" secondary="ETH publishes cohort-level starting-salary distribution annually" />
          <RegionSource flag="🇨🇦" region="Canada" primary="Statistics Canada (Labour Force Survey by NOC) + provincial Post-Secondary Outcomes surveys" secondary="Firm-level bands via Levels.fyi Toronto + Big-4 Canadian pay scales" />
          <RegionSource flag="🇸🇬" region="Singapore" primary="MOE Joint Autonomous Universities Graduate Employment Survey (published annually)" secondary="MoM Occupational Wage Table + firm careers pages" />
          <RegionSource flag="🇭🇰" region="Hong Kong" primary="UGC-GDCS Graduate Employment Survey (annual, per-uni cohort)" secondary="Firm bands cross-checked against JobsDB + Hays Salary Guide HK" />
        </div>
        <p className="mt-4 text-[12px] text-stone-500 leading-relaxed">
          The "editorial · …" tag means our editors compile the read
          themselves from the public sources listed — we don't proxy a
          third-party aggregator. When we can't verify a claim against
          at least two independent public sources, we mark it{" "}
          <em>estimated</em> and add a caveat chip on the firm profile
          page.
        </p>
      </Section>

      <Section id="editorial-curation" title="Editorial curation — where APIs don't publish it">
        <p>
          For anything an official register doesn't publish — firm hiring
          patterns, salary bands, city write-ups — we do our own research
          and label the provenance visibly on every surface so families
          know what they're looking at.
        </p>
        <div className="mt-4 space-y-3">
          <EditorialRow
            name="Firm feeder pipelines"
            what="Which universities feed into which of 715 firms (McKinsey, Goldman, MI5, etc.)"
            why="HESA Graduate Outcomes publishes SOC/SIC codes but not the actual firm names — the piece families most want."
            how="Curated from public firm target-school lists, published campus-recruiting rotas, LinkedIn alumni patterns. Rendered as Signal Bands (Strong / Regular / Emerging) — never as pseudo-audited percentages. Every row carries a PATTERN chip + a source note."
          />
          <EditorialRow
            name="Firm salary bands"
            what="Base salary + total comp bands for early-career roles"
            why="Firms rarely publish audited bands. Families still need a directional guide."
            how="Sector medians from Levels.fyi + Glassdoor for peer firms. Every band carries an amber 'Unverified · sector benchmark' chip with a tooltip. Firms on our Verified Partner tier can publish their own with a mandatory source URL — those render with an emerald 'Officially supplied by' chip that outranks the benchmark."
          />
          <EditorialRow
            name="City guides + campus write-ups"
            what="Editorial content on cities and campus life"
            why="Practical context APIs don't cover — food, weather, safety, transit."
            how="Drafted by our editors from ONS + university prospectuses + firm-hub location notes, then read against those same primary sources before publication. Tagged with content_source in the DB so anything that hasn't been human-checked in the last 12 months surfaces on the corrections queue."
          />
        </div>
      </Section>

      <Section id="gaps" title="What we don't yet cover — honestly">
        <ul className="list-disc pl-5 space-y-1.5 text-[14px]">
          <li>
            <strong>German-taught degrees at German universities</strong> —
            ~15,000 programmes on Hochschulkompass. No open API; ingest
            blocked until they publish one or we license a mirror.
          </li>
          <li>
            <strong>Sweden</strong> — Susa-navet API integration pending correct
            Swagger URL from the provider.
          </li>
          <li>
            <strong>~274 US institutions</strong> haven't yet been matched to
            our canonical WUA IDs (Scorecard entities without a clear slug).
          </li>
          <li>
            <strong>Postgraduate-only courses</strong> in some non-UK countries.
            Coverage is undergraduate-first.
          </li>
        </ul>
        <p className="mt-3 text-[13px] text-stone-500">
          Spot a source we should be using but aren't? Email{" "}
          <a href="mailto:hello@whichuniversityadvisor.com" className="text-[#4C1D95] underline">
            hello@whichuniversityadvisor.com
          </a>
          .
        </p>
      </Section>

      <Section id="corrections" title="Errors and corrections">
        <p>
          We publish every material data correction we make with the
          date it was applied and the source that flagged it. If you
          find a number that looks wrong, please email{" "}
          <a
            href="mailto:corrections@whichuniversityadvisor.com"
            className="text-[#4C1D95] underline"
          >
            corrections@whichuniversityadvisor.com
          </a>
          {" "}with the URL and the correct value. We review within 48
          hours.
        </p>
        <Link
          to="/methodology/corrections"
          data-testid="methodology-corrections-link"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95] bg-[#4C1D95]/[0.03] px-3 py-1.5 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white"
        >
          Read the corrections log
          <ExternalLink className="h-3 w-3" />
        </Link>
      </Section>

      <Section id="editorial-process" title="Editorial process">
        <p>
          Every page on WhichUniversityAdvisor is written or reviewed by
          our editors before it publishes. The core facts — acceptance
          rates, tuition, deadlines, cohort earnings, top employers —
          come straight from public regulator datasets (see the source
          list above); our editors write the read-back: the framing,
          the caveats, the "what this really means for your family"
          notes that regulator dumps don't carry.
        </p>
        <p className="mt-3">
          For the long tail of directory-tier institutions (~284 UK FE
          colleges + specialist HE providers) we accelerate the first
          draft with a machine-assisted fact-sheet, produced under a
          strict{" "}
          <em>use only the facts explicitly supplied</em>{" "}
          rule — no invented alumni, no unverified programme claims,
          no ranking-style adjectives. Every draft is then read by a
          human editor against the source fact-sheet before it
          publishes, and each one carries a{" "}
          <code className="rounded bg-stone-100 px-1">content_source</code>{" "}
          tag in the database so we can spot-audit anything that
          hasn't been re-verified in the last 12 months. The raw
          prompt template is public in{" "}
          <code className="rounded bg-stone-100 px-1">
            /app/backend/scripts/promote_uk_directory.py
          </code>
          {" "}for anyone who wants to see exactly what constraints
          our editors work under.
        </p>
        <p className="mt-3 text-[13px] text-stone-500">
          If a phrase reads like it was written by a machine, that's a
          bug — email{" "}
          <a
            href="mailto:corrections@whichuniversityadvisor.com"
            className="text-[#4C1D95] underline"
          >
            corrections@whichuniversityadvisor.com
          </a>
          {" "}and we'll rewrite it.
        </p>
      </Section>

      <Section id="not-a-ranking" title="Why we are NOT a ranking site (and where we are, honestly)">
        <p>
          THE, QS and US News each publish a single-number
          "prestige" score. Those scores are opaque, licensed for
          republication at premium rates, and don't answer any of
          the four questions a real family asks: Can I get in? Can
          my family afford it? Will I enjoy it? Will it take me
          where I want to go?
        </p>
        <p className="mt-3">
          We do carry world_rank as one comparison signal where
          publicly quoted, but we do NOT compute our own global
          ranking and we never sell rank-position boosts to
          universities. The "best university" is a decision, not a
          number.
        </p>
        <div className="mt-4 rounded-md border border-[#4C1D95]/40 bg-[#4C1D95]/5 p-4"
             data-testid="methodology-subject-rankings-note">
          <div className="label-uppercase !text-[10px] text-[#4C1D95] mb-2">
            The one ranking we DO publish — subject × country outcomes
          </div>
          <p className="text-sm">
            Where we have verified per-uni-per-subject median graduate
            salary (HESA/LEO for UK, Scorecard for US, QILT for AU,
            StatCan for CA), we compute an <strong>outcomes-revealed
            leaderboard</strong> on{" "}
            <a href="/rankings" className="underline text-[#4C1D95]">
              /rankings
            </a>. It's a weighted composite of five signals: 35% median
            salary, 20% salary-to-tuition ratio, 15% admissibility,
            15% student satisfaction, 15% completion + retention.
            Weights renormalise for missing signals. Every input is on
            the page next to every rank — you can rebuild the composite
            yourself. Rankings are ONLY published for country × subject
            pairs where at least 8 unis pass the verified-salary filter;
            below that we suppress the leaderboard rather than mislead.
          </p>
        </div>
      </Section>

      <Section id="chance-score" title="How the admission-chance score works">
        <p>
          When you run a Quick Check, every course you match against
          gets two numbers side-by-side: a rule-based{" "}
          <strong>gap score</strong> (a signed value between −1 and +1
          comparing your reported profile to the university's published
          entry band), and — when we have the uni's UCAS acceptance
          rate on file — a <strong>calibrated admission probability</strong>{" "}
          expressed as a real percentage. Both are shown so families
          can see the raw signal AND a friendlier translation.
        </p>

        <div className="mt-4 rounded-md border border-[#4C1D95]/40 bg-[#4C1D95]/5 p-4"
             data-testid="methodology-probability-explainer">
          <div className="label-uppercase !text-[10px] text-[#4C1D95] mb-2">
            The calibrated admission probability (Path A — v1)
          </div>
          <div className="text-[13px] text-stone-800 space-y-2">
            <p>
              <span className="font-mono bg-white border border-stone-200 rounded px-1.5 py-0.5">
                P(admit) = clip( base_rate × gap_multiplier, 2%, 98% )
              </span>
            </p>
            <p>
              <strong>base_rate</strong> is the university's UCAS
              End-of-Cycle acceptance rate — the real{" "}
              <em>accepted / applied</em> ratio, published by UCAS and
              covering ~99% of UK providers. For US courses we use
              College Scorecard admit rate; for a small number of
              verified courses we use the per-course offer rate.
            </p>
            <p>
              <strong>gap_multiplier</strong> is a smooth piecewise
              curve anchored so that gap = 0 (right at typical) gives a
              multiplier of 1.0 (i.e. the base rate is unchanged), gap
              ≥ +0.50 doubles it, gap = −0.60 shrinks it to 0.20×, and
              anything below "Highly Ambitious" decays exponentially to
              a 5% floor. The shape is calibrated against{" "}
              publicly-reported admit-by-grade-band tables (Oxford
              Norrington data, US Common Data Sets).
            </p>
            <p>
              <strong>Clipping</strong> caps the result at 2%–98% so a
              50%-acceptance-rate uni + gap +0.5 doesn't display "100%
              chance" (which would be false precision).
            </p>
            <p className="text-stone-600 text-[12px] italic">
              This is a calibrated <em>estimate</em>, not a
              statistical model trained on real outcomes. We're
              beginning to log anonymous Quick-Check submissions today
              (Path B) so we can regress a proper model in late 2026 —
              at which point the number here upgrades transparently
              with the model version tag.
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-stone-200 bg-white p-4">
          <div className="label-uppercase !text-[10px] text-stone-600 mb-2">
            Gap-score → bucket thresholds
          </div>
          <div className="text-[13px] font-mono space-y-1">
            <div><span className="text-emerald-700 font-semibold">Likely</span>            — gap ≥ +0.50 (comfortably above typical)</div>
            <div><span className="text-amber-700 font-semibold">Target</span>              — gap ≥ −0.15 (around typical admitted profile)</div>
            <div><span className="text-orange-700 font-semibold">Ambitious</span>          — gap ≥ −0.60 (below typical, above published minimum)</div>
            <div><span className="text-red-700 font-semibold">Highly Ambitious</span>      — gap &lt; −0.60 (below published minimum on grades / subjects / tests)</div>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-stone-200 bg-white p-4">
          <div className="label-uppercase !text-[10px] text-stone-600 mb-2">
            What goes into the number
          </div>
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left border-b border-stone-200 font-underground-sc text-[10px] text-stone-500 tracking-wider">
                <th className="py-1.5 pr-2">Factor</th>
                <th className="py-1.5 pr-2">Weight</th>
                <th className="py-1.5">Notes</th>
              </tr>
            </thead>
            <tbody className="text-stone-700">
              <tr className="border-b border-stone-100"><td className="py-1.5 pr-2 font-mono">Grades</td><td className="py-1.5 pr-2">30%</td><td className="py-1.5">Signed gap vs the uni's typical band, floored at −1 grade.</td></tr>
              <tr className="border-b border-stone-100"><td className="py-1.5 pr-2 font-mono">Subject gates</td><td className="py-1.5 pr-2">30%</td><td className="py-1.5">Per-subject minima (Maths / English) — a critical miss caps the bucket.</td></tr>
              <tr className="border-b border-stone-100"><td className="py-1.5 pr-2 font-mono">Test score</td><td className="py-1.5 pr-2">15%</td><td className="py-1.5">SAT / ACT — penalised only when the uni requires one.</td></tr>
              <tr className="border-b border-stone-100"><td className="py-1.5 pr-2 font-mono">English proficiency</td><td className="py-1.5 pr-2">10%</td><td className="py-1.5">Waived on non-English-taught courses.</td></tr>
              <tr className="border-b border-stone-100"><td className="py-1.5 pr-2 font-mono">Activities</td><td className="py-1.5 pr-2">10%</td><td className="py-1.5">Self-rated 1–5.</td></tr>
              <tr><td className="py-1.5 pr-2 font-mono">Portfolio / interview</td><td className="py-1.5 pr-2">5%</td><td className="py-1.5">Applies when the course requires one; audition-led courses reweight to 70% here + 30% activities.</td></tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 rounded-md border border-stone-200 bg-white p-4">
          <div className="label-uppercase !text-[10px] text-stone-600 mb-2">
            Sub-bands — the "just inside Target · 0.20 to Likely" chip
          </div>
          <p className="text-[13px]">
            The pill next to each result tells you where <em>inside</em>
            the bucket you sit — high, middle, or low — plus the numeric
            distance to the next band up. That way you can see the
            difference between a student who's a whisker below Likely
            and one who's a whisker above Ambitious even though both
            classify as "Target".
          </p>
        </div>

        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50/40 p-4">
          <div className="label-uppercase !text-[10px] text-amber-900 mb-2">
            Data quality — <em>Indicative fit</em> and <em>Partial data</em> chips
          </div>
          <p className="text-[13px] text-stone-800">
            Not every university on the site publishes its entry band in
            the same detail — this is especially true outside the UK, US
            and Australia. When we can't back the bucket with a firm
            grade comparison, we downgrade the chip:
          </p>
          <ul className="mt-2 text-[13px] list-disc pl-5 space-y-1 text-stone-800">
            <li>
              <strong>Partial data</strong> — one of your inputs
              was translated across curricula (e.g. Abitur → A-Level via
              our anchor table), or a required signal like SAT was
              missing. The bucket is still directional but read it as a
              range.
            </li>
            <li>
              <strong>Indicative fit — limited data</strong> — the
              university has no published entry band on our record, or
              your curriculum couldn't be mapped to any band it does
              publish. Treat this as "worth a closer look", not a
              probability. Talk to admissions directly for a firmer read.
            </li>
          </ul>
        </div>

        <div className="mt-4 rounded-md border border-stone-200 bg-white p-4">
          <div className="label-uppercase !text-[10px] text-stone-600 mb-2">
            What it is NOT
          </div>
          <ul className="text-[13px] list-disc pl-5 space-y-1 text-stone-800">
            <li>
              It's <strong>not a statistical prediction</strong> trained
              on real admit / reject outcomes — we don't yet have that
              historical dataset. The calibrated probability
              (explained above) is anchored on real UCAS acceptance
              rates + a mathematically-shaped multiplier, but it is
              not yet regressed against outcomes. Path B (empirical
              calibration from our own submissions data) is
              scheduled for late 2026.
            </li>
            <li>
              It's <strong>not a scoring of the university</strong>. Two
              students can rate the same course very differently — the
              gap score is <em>always</em> relative to <em>your</em>
              reported profile.
            </li>
            <li>
              It <strong>can be gamed by mis-reporting grades</strong>.
              The score is only as honest as the inputs.
            </li>
          </ul>
        </div>

        <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50/40 p-4 text-[13px] text-stone-800">
          <strong>Full source:</strong> the exact weights, thresholds and
          band floors live in{" "}
          <code className="rounded bg-white px-1 border border-stone-200">
            /app/backend/ai_advisor.py
          </code>
          {" "}(`RULES` dict at the top). Editors change one file to
          re-tune the model; there is no black-box weight.
        </div>
      </Section>

      <div className="mt-10 rounded-lg border border-emerald-300 bg-emerald-50/40 p-5 text-[13px] text-stone-700 leading-snug">
        <div className="font-editorial text-lg text-[#002147] flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          The commitment
        </div>
        <p className="mt-2">
          If we can't cite a number, we don't publish it. If we make
          an error, we fix it publicly. If a family follows our
          advice and it doesn't work out, we want to know so we can
          improve the model. That's the whole methodology.
        </p>
      </div>

      <div
        data-testid="methodology-parent-attribution"
        className="mt-6 rounded-md border border-stone-200 bg-white p-4 text-[12px] text-stone-600 leading-relaxed"
      >
        WhichUniversityAdvisor.com is a service of{" "}
        <a
          href="https://which-media.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#4C1D95] underline"
        >
          Which Media FZ LLC
        </a>
        , the independent media group also behind adjacent
        education-and-mobility products. Editorial control sits
        entirely with the WUA newsroom; commercial deals with
        universities or employers never affect the data taxonomy,
        the tier a school lands in, or the corrections log.
      </div>

      <div className="mt-8 text-[13px] text-stone-500 flex items-center gap-2">
        <Compass className="h-3 w-3" />
        Also see:{" "}
        <Link to="/methodology/corrections" className="text-[#4C1D95] underline">
          corrections log
        </Link>
        {" · "}
        <Link to="/pathways" className="text-[#4C1D95] underline">
          admission pathways
        </Link>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} data-testid={`methodology-section-${id}`} className="mt-10">
      <div className="label-uppercase text-[#4C1D95] font-semibold">{title}</div>
      <div className="mt-3 text-[14px] text-stone-700 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function TierRow({ name, colour, body }) {
  return (
    <li className="flex items-start gap-3">
      <span
        className={
          "shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-underground-sc tracking-wider mt-[3px] " +
          colour
        }
      >
        {name}
      </span>
      <span>{body}</span>
    </li>
  );
}

function SourceRow({ field, source, url, refresh, lastUpdated }) {
  return (
    <tr className="border-b border-stone-100">
      <td className="py-2 pr-2 font-medium text-[#002147]">{field}</td>
      <td className="py-2 pr-2">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-[#4C1D95] hover:underline inline-flex items-center gap-1"
          >
            {source} <ExternalLink className="h-3 w-3 opacity-70" />
          </a>
        ) : (
          source
        )}
      </td>
      <td className="py-2 pr-2 font-mono text-[11px] text-stone-500">{refresh}</td>
      <td className="py-2 font-mono text-[11px] text-stone-500">{lastUpdated || "—"}</td>
    </tr>
  );
}

function EditorialRow({ name, what, why, how }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4" data-testid={`methodology-editorial-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <h3 className="font-semibold text-stone-900">{name}</h3>
      <dl className="mt-2 space-y-1.5 text-[13px] text-stone-700">
        <div className="flex gap-2">
          <dt className="w-14 shrink-0 text-[10px] font-underground-sc tracking-wider text-stone-400 pt-0.5">What</dt>
          <dd>{what}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-14 shrink-0 text-[10px] font-underground-sc tracking-wider text-stone-400 pt-0.5">Why</dt>
          <dd>{why}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-14 shrink-0 text-[10px] font-underground-sc tracking-wider text-stone-400 pt-0.5">How</dt>
          <dd>{how}</dd>
        </div>
      </dl>
    </div>
  );
}


function StatTile({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3">
      <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">{label}</div>
      <div className="mt-1 font-editorial text-2xl text-[#002147] tabular-nums">{value ?? "—"}</div>
      {sub && <div className="text-[11px] font-mono text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}


function RegionSource({ flag, region, primary, secondary }) {
  return (
    <div
      className="rounded-lg border border-stone-200 bg-white p-4"
      data-testid={`methodology-region-${region.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2">
        <span aria-hidden className="text-lg">{flag}</span>
        <h3 className="font-semibold text-[#002147]">{region}</h3>
      </div>
      <dl className="mt-2 space-y-1.5 text-[12px]">
        <div className="flex gap-2">
          <dt className="w-16 shrink-0 text-[10px] font-underground-sc tracking-wider text-stone-400 pt-0.5">Primary</dt>
          <dd className="text-stone-700">{primary}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-16 shrink-0 text-[10px] font-underground-sc tracking-wider text-stone-400 pt-0.5">Extras</dt>
          <dd className="text-stone-600">{secondary}</dd>
        </div>
      </dl>
    </div>
  );
}

